import { useContext, useEffect, useState } from 'react';

import Button from '../../reusable/button/button.component';
import Spinner from '../../reusable/spinner/spinner.component';

import { ToastContext } from '../../../contexts/toast.context';
import Client from '../../../tools/client';

import {
  ContentContainer,
  MainTitle,
  Subtitle,
  InfoBlock,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody
} from '../../../styles/component.styles';

const client = new Client();

const ReadyToFulfillDisplay = ({ refresh }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const [orders, setOrders] = useState([]);
  const [unshipped, setUnshipped] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [fulfillmentQuantities, setFulfillmentQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { successToast, errorToast } = useContext(ToastContext);

  useEffect(() => {
    getReadyToFulfill();
  }, []);

  const getReadyToFulfill = async () => {
    setLoading(true);
    try {
      const res = await client.getReadyToFulfill();
      setSummary(res.pullSummary);
      setOrders(res.breakdownByOrder);

      const res2 = await client.getIncompleteFulfillments(); // unshipped fulfillments
      setUnshipped(res2 || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const handleQuantityChange = (orderItemId, value, maxQty) => {
    let qty = parseInt(value);
    if (isNaN(qty) || qty < 0) qty = 0;
    if (qty > maxQty) qty = maxQty;

    setFulfillmentQuantities((prev) => ({
      ...prev,
      [orderItemId]: qty
    }));
  };

  const handleCreateFulfillment = async () => {
    const orderIds = Object.keys(selectedOrders).filter((id) => selectedOrders[id]);
    if (orderIds.length === 0) return;

    const data = {
      fulfillments: []
    };

    for (const order of orders) {
      if (!selectedOrders[order.id]) continue;

      const items = order.products
        .map((p) => {
          const quantity = fulfillmentQuantities[p.orderItemId] ?? p.quantity;
          return quantity > 0 ? { orderItemId: p.orderItemId, quantity } : null;
        })
        .filter(Boolean);

      if (items.length > 0) {
        data.fulfillments.push({
          orderId: order.id,
          items
        });
      }
    }

    if (data.fulfillments.length === 0) {
      errorToast('No valid fulfillment items selected.');
      return;
    }

    setSubmitting(true);

    try {
      await client.createBatchFulfillments(data);
      successToast(`Fulfillments created successfully.`);
      setSelectedOrders({});
      setFulfillmentQuantities({});
      refresh?.();
      await getReadyToFulfill();
    } catch (err) {
      console.error('Create Fulfillment Error:', err);
      errorToast('Failed to create fulfillments.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <ContentContainer>
      <MainTitle>Pull Summary</MainTitle>
      {summary.length === 0 ? (
        <Subtitle>No items to pull.</Subtitle>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>New/Billed</TableHead>
              <TableHead>Paid/Processing</TableHead>
              <TableHead>Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.map((item, idx) => (
              <TableRow key={idx}>
                <td>{item.productName}</td>
                <td>{item.inventoryDetails?.sku || '—'}</td>
                <td>{item.inventoryDetails?.size || '—'}</td>
                <td>{item.fromNewOrBilled}</td>
                <td>{item.fromPaidOrProcessing}</td>
                <td>{item.totalToPull}</td>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <MainTitle>Orders to Fulfill</MainTitle>
      {orders.length === 0 ? (
        <Subtitle>No orders ready to fulfill.</Subtitle>
      ) : (
        orders.map((order) => (
          <InfoBlock key={order.id}>
            <label>
              <input
                type="checkbox"
                checked={!!selectedOrders[order.id]}
                onChange={() => toggleOrderSelection(order.id)}
              />{' '}
              <strong>Order #{order.refId}</strong> ({order.status})
            </label>
            <ul>
              {order.products.map((p, idx) => (
                <li key={idx}>
                  Inventory ID: {p.inventoryId}, Quantity: {p.quantity}{' '}
                  <input
                    type="number"
                    min="0"
                    max={p.quantity}
                    value={fulfillmentQuantities[p.orderItemId] ?? p.quantity}
                    onChange={(e) =>
                      handleQuantityChange(p.orderItemId, e.target.value, p.quantity)
                    }
                    style={{ width: '60px', marginLeft: '10px' }}
                  />
                </li>
              ))}
            </ul>
          </InfoBlock>
        ))
      )}

      {orders.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <Button
            onClick={handleCreateFulfillment}
            disabled={submitting || Object.values(selectedOrders).every((v) => !v)}
          >
            {submitting ? 'Creating...' : 'Create Fulfillments'}
          </Button>
        </div>
      )}
    </ContentContainer>
  );
};

export default ReadyToFulfillDisplay;